import { useTranslation } from "react-i18next";
import Logo from "../assets/img/logos/X.svg";
import { Checkbox, CircularProgress, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { AcceptTermsModal } from "../components/modals/AcceptTermsModal";
import { isHiltonCompany } from "../utils/hilton-branch";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "../context/toast-context";
import { UserService } from "../services/user-service";
import { UnverifiedUser } from "../services/dto/response/user-response-dto";
import { Loading } from "../components/Loading";
import { DNIUploader } from "../components/DniUploader";
import { compressImage } from "../utils/compress-image";
import { SelfieUploader } from "../components/SelfieUploader";
import { LocationSelectors } from "../components/LocationSelectors";
import { COUNTRIES, getCountryCode } from "../constants/countries";
import { DOCUMENT_LENGTHS } from "../constants/validations";

export function VerifyManager() {
  const { t } = useTranslation();

  const [manager, setManager] = useState<UnverifiedUser | null>(null);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    identityNumber: "",
    phone: "",
    departmentId: 0,
    cityId: 0
  });

  const [frontDNI, setFrontDNI] = useState<File | null>(null);
  const [backDNI, setBackDNI] = useState<File | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userService = useMemo(() => new UserService(), []);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const userid = params.get("userid");
  const token = params.get("token");
  const managerCountryCode = manager?.countryCode
    ? getCountryCode(manager.countryCode)
    : null;
  const isDominicanRepublic = managerCountryCode === COUNTRIES.DO;
  const cuitLabel = isDominicanRepublic ? "Cédula" : "CUIL";
  const isHilton = isHiltonCompany(manager?.companyName);

  const validate = (): boolean => {
    let isValid: boolean = true;
    const newErrors: { [key: string]: string } = {};

    const isFormValid =
      password === confirmPassword && password !== "" && isTermsChecked;
    if (!isFormValid) {
      newErrors.password = t("employee.verify.errors.password");
      isValid = false;
    }

    if (!formData.firstName?.trim()) {
      newErrors.firstName = t("managers.create.errors.name");
      isValid = false;
    } else if (formData.firstName.length < 3) {
      newErrors.firstName = t("managers.create.errors.name-min");
      isValid = false;
    } else if (formData.firstName.length > 20) {
      newErrors.firstName = t("managers.create.errors.name-max");
      isValid = false;
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = t("managers.create.errors.lastName");
      isValid = false;
    } else if (formData.lastName.length < 3) {
      newErrors.lastName = t("managers.create.errors.lastName-min");
      isValid = false;
    } else if (formData.lastName.length > 30) {
      newErrors.lastName = t("managers.create.errors.lastName-max");
      isValid = false;
    }

    if (!formData.identityNumber) {
      newErrors.identityNumber = t("managers.create.errors.identityNumber");
      isValid = false;
    } else {
      const expectedLength = isDominicanRepublic 
        ? DOCUMENT_LENGTHS.DO_CEDULA_LENGTH 
        : DOCUMENT_LENGTHS.AR_CUIL_LENGTH;
      
      if (formData.identityNumber.length !== expectedLength) {
        newErrors.identityNumber = t("managers.create.errors.identityNumber-min");
        isValid = false;
      } else if (
        !isDominicanRepublic &&
        !formData.identityNumber.startsWith("20") &&
        !formData.identityNumber.startsWith("23") &&
        !formData.identityNumber.startsWith("24") &&
        !formData.identityNumber.startsWith("27")
      ) {
        newErrors.identityNumber = t("managers.create.errors.identityNumber-note");
        isValid = false;
      }
    }

    if (!formData.address?.trim()) {
      newErrors.address = t("managers.create.errors.address");
      isValid = false;
    } else if (formData.address.length < 5) {
      newErrors.address = t("managers.create.errors.address-min");
      isValid = false;
    } else if (formData.address.length > 50) {
      newErrors.address = t("managers.create.errors.address-max");
      isValid = false;
    }

    if (!formData.phone) {
      newErrors.phone = t("managers.create.errors.phone-required");
      isValid = false;
    } else if (formData.phone.length < 7 || formData.phone.length > 20) {
      newErrors.phone = t("managers.create.errors.phone-size");
      isValid = false;
    }

    if (!isDominicanRepublic) {
      if (!formData.departmentId || formData.departmentId === 0) {
        newErrors.departmentId = t("managers.create.errors.departmentId");
        isValid = false;
      }

      if (!formData.cityId || formData.cityId === 0) {
        newErrors.cityId = t("managers.create.errors.cityId");
        isValid = false;
      }
    }

    // Validación de documentos
    if (!frontDNI || !backDNI || !selfie) {
      const missingDocs: string[] = [];
      if (!frontDNI) missingDocs.push(t("managers.create.errors.managerDniFront"));
      if (!backDNI) missingDocs.push(t("managers.create.errors.managerDniBack"));
      if (!selfie) missingDocs.push(t("managers.create.errors.managerSelfie"));

      newErrors.dniDocuments = `${t("managers.create.errors.dniDocuments")}: ${missingDocs.join(", ")}`;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTermsChecked((prev) => !prev);
  };

  const handleAcceptTerms = () => {
    setIsTermsChecked(true);
    setIsOpenModal(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setIsVerifying(true);
    e.preventDefault();
    if (!validate()) {
      setIsVerifying(false);
      return;
    }

    const compressedFrontDNI = await compressImage(frontDNI!);
    const compressedBackDNI = await compressImage(backDNI!);
    const compressedSelfie = await compressImage(selfie!);

    await userService.verifyManager(
      {
        Password: password,
        Token: token!,
        UserId: Number(userid!),
        DocumentBack: compressedBackDNI,
        DocumentFront: compressedFrontDNI,
        DocumentSelfie: compressedSelfie,
        FirstName: formData.firstName,
        LastName: formData.lastName,
        Email: manager!.email,
        Address: formData.address,
        IdentityNumber: formData.identityNumber,
        Phone: formData.phone,
        DepartmentId: !isDominicanRepublic ? formData.departmentId : undefined,
        CityId: !isDominicanRepublic ? formData.cityId : undefined,
        CountryCode: manager!.countryCode,
      },
      () => {
        setIsVerifying(false);
        navigate("/account-verified");
      },
      (error) => {
        setIsVerifying(false);
        showToast(error, "error");
      }
    );
  };

  useEffect(() => {
    const fetchManager = async () => {
      setIsLoading(true);
      const controller = new AbortController();
      const { signal } = controller;

      try {
        await userService.getUnverifiedManager(
          {
            token: token!,
            userid: Number(userid!),
          },
          (data) => {
            setManager(data);
            const nameParts = data.name.split(" ");
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";
            
            setFormData({
              firstName: firstName,
              lastName: lastName,
              address: data.address || "",
              identityNumber: data.identityNumber || "",
              phone: data.phone || "",
              departmentId: data.paycloudDepartmentId || 0,
              cityId: data.paycloudCityId || 0
            });
            setPassword("");
            setConfirmPassword("");
          },
          (error) => {
            setErrors({ verified: "Usuario verificado" });
          }
        );
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    if (userid && token) {
      fetchManager();
    }
  }, [token, userid, userService]);

  return (
    <main className="h-screen container mx-auto">
      <div className="flex flex-col mx-auto justify-center items-center pt-12 px-4 md:px-0">
        <img className="w-14 h-14" src={Logo} alt="Divix Logo" />
        {!!errors.verified && !isLoading && (
          <div className="mt-20 text-xl text-center font-semibold">
            <p>{errors.verified}</p>
          </div>
        )}
        {!errors.verified && isLoading && <Loading sidebar={false} />}
        {!errors.verified && !isLoading && manager && (
          <>
            <h1 className="font-normal text-center tracking-wide text-3xl md:text-5xl pt-14">
              {t("verify-account.welcome")}
            </h1>
            <span className="text-xl md:text-2xl pt-6 text-center font-normal max-w-2xl">
              {t("verify-account.subtitle")}
            </span>

            <form
              className="mt-6 max-w-sm mx-auto w-full"
              onSubmit={handleSubmit}
            >
              <input 
                type="hidden" 
                name="username" 
                value={manager?.email || ''} 
                autoComplete="username"
              />
              <div className="grid gap-2">
                <TextField
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  label={t("managers.create.name")}
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  variant="standard"
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#d32f2f",
                      marginLeft: 0,
                      marginTop: "3px",
                      fontSize: "0.75rem",
                    },
                  }}
                />
                <TextField
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  label={t("managers.create.lastName")}
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  variant="standard"
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#d32f2f",
                      marginLeft: 0,
                      marginTop: "3px",
                      fontSize: "0.75rem",
                    },
                  }}
                />
                <TextField
                  error={!!errors.identityNumber}
                  helperText={errors.identityNumber}
                  label={cuitLabel}
                  name="identityNumber"
                  type="text"
                  value={formData.identityNumber}
                  onChange={handleInputChange}
                  onInput={(e) => {
                    const input = e.target as HTMLInputElement;
                    const maxLength = isDominicanRepublic 
                      ? DOCUMENT_LENGTHS.DO_CEDULA_LENGTH 
                      : DOCUMENT_LENGTHS.AR_CUIL_LENGTH;
                    input.value = input.value.replace(/[^\d]/g, "").slice(0, maxLength);
                  }}
                  variant="standard"
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#d32f2f",
                      marginLeft: 0,
                      marginTop: "3px",
                      fontSize: "0.75rem",
                    },
                  }}
                />
                <TextField
                  error={!!errors.address}
                  helperText={errors.address}
                  label={t("managers.create.address")}
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleInputChange}
                  variant="standard"
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#d32f2f",
                      marginLeft: 0,
                      marginTop: "3px",
                      fontSize: "0.75rem",
                    },
                  }}
                />
                <TextField
                  error={!!errors.phone}
                  helperText={errors.phone}
                  label={t("managers.create.phone")}
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onInput={(e) => {
                    const input = e.target as HTMLInputElement;
                    input.value = input.value.replace(/[^\d]/g, "").slice(0, 20);
                  }}
                  autoComplete="tel"
                  variant="standard"
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#d32f2f",
                      marginLeft: 0,
                      marginTop: "3px",
                      fontSize: "0.75rem",
                    },
                  }}
                />
                <TextField
                  error={!!errors.password}
                  helperText={errors.password}
                  label={
                    <div className="flex items-center gap-4">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4 6V4a4 4 0 1 1 8 0v2h2v10H2V6h2Zm2-2a2 2 0 1 1 4 0v2H6V4Zm1 9V9h2v4H7Z"
                          fill="#000"
                        />
                      </svg>
                      {t("change-password.new")}
                    </div>
                  }
                  variant="standard"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                      fontSize: "1rem",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                  }}
                />
                <TextField
                  error={!!errors.password}
                  helperText={errors.password}
                  label={
                    <div className="flex items-center gap-4">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4 6V4a4 4 0 1 1 8 0v2h2v10H2V6h2Zm2-2a2 2 0 1 1 4 0v2H6V4Zm1 9V9h2v4H7Z"
                          fill="#000"
                        />
                      </svg>
                      {t("change-password.repeat")}
                    </div>
                  }
                  variant="standard"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  sx={{
                    "& .MuiInput-root": {
                      color: "#000",
                      fontFamily: "Montserrat",
                      "&:before": {
                        borderBottom: "1px solid #838383",
                      },
                      "&:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#000",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#838383",
                    },
                    "& .MuiInputLabel-standard": {
                      color: "#838383",
                      fontWeight: "300",
                      fontFamily: "Montserrat",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#838383",
                    },
                  }}
                />

                {!isDominicanRepublic && (
                  <>
                    <LocationSelectors
                      initialDepartmentId={formData.departmentId > 0 ? formData.departmentId.toString() : ""}
                      initialCityId={formData.cityId > 0 ? formData.cityId.toString() : ""}
                      onProvinceChange={(provinceId) => {
                        setFormData(prev => ({
                          ...prev,
                          departmentId: parseInt(provinceId),
                          cityId: 0
                        }));
                        setErrors(prev => ({ ...prev, departmentId: "" }));
                      }}
                      onCityChange={(cityId) => {
                        setFormData(prev => ({
                          ...prev,
                          cityId: parseInt(cityId)
                        }));
                        setErrors(prev => ({ ...prev, cityId: "" }));
                      }}
                    />
                    {(errors.departmentId || errors.cityId) && (
                      <p className="text-red text-xs mt-1">
                        {errors.departmentId || errors.cityId}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="text-center mt-4">
                <div className="flex flex-col gap-4 border rounded-lg p-4">
                  <DNIUploader
                    countryCodeOverride={managerCountryCode}
                    onFilesChange={(front, back) => {
                      setFrontDNI(front);
                      setBackDNI(back);
                    }}
                  />
                  <SelfieUploader
                    fileName={manager.name}
                    onFileChange={(file) => {
                      setSelfie(file);
                    }}
                  />
                </div>
                {errors.dniDocuments && (
                  <p className="text-red mt-2 text-xs">{errors.dniDocuments}</p>
                )}
              </div>
              <div className="w-full flex justify-center gap-1 items-center text-center mx-auto mt-8 px-2">
                <Checkbox
                  checked={isTermsChecked}
                  onChange={handleCheckboxChange}
                  color="primary"
                  sx={{
                    color: "#000",
                    padding: 0,
                    "&.Mui-checked": {
                      color: "#EF0BB8",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 16,
                      margin: 0,
                      padding: 0,
                    },
                  }}
                />
                <div className="text-xs font-semibold">
                  Acepto{" "}
                  <span
                    className="hover:underline cursor-pointer"
                    onClick={() => setIsOpenModal(true)}
                  >
                    Términos & Condiciones
                  </span>
                </div>
              </div>
              <div className="w-full mx-auto mt-6">
                <button
                  type="submit"
                  disabled={!isTermsChecked || isVerifying}
                  className={`w-full leading-5 bg-black text-white font-light rounded-3xl py-1 px-4 disabled:opacity-50 ${
                    isTermsChecked
                      ? "hover:bg-black/90 opacity-100"
                      : "opacity-50"
                  } `}
                >
                  {isVerifying ? (
                    <CircularProgress
                      sx={{
                        color: "#fff",
                      }}
                      size="1.5rem"
                      className="animate-spin "
                    />
                  ) : (
                    t("verify-account.submit")
                  )}
                </button>
              </div>
            </form>

            <span className="py-6 font-medium text-lg">
              {t("verify-account.bye")}
            </span>
          </>
        )}
      </div>
      {isOpenModal && (
        <AcceptTermsModal
          onAccept={handleAcceptTerms}
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          useHilton={isHilton}
        />
      )}
    </main>
  );
}
